import { supabase } from '../lib/supabase';

export const PRODUCTION_RESPONSIBLE_TEAM = 'production_responsible';

/** Ancienne valeur en base ; équivalente pour le parcours atelier. */
export const LEGACY_PRODUCTION_TEAM = 'production_team';

export const PRODUCTION_ATELIER_RESPONSIBLE_TEAMS = [
  PRODUCTION_RESPONSIBLE_TEAM,
  LEGACY_PRODUCTION_TEAM,
];

/** Lot 1 : parcours production atelier à partir de la pré-production (ordre 4+). */
export const GROUP1_WORKSHOP_MIN_PHASE_ORDER = 4;

function resolveMilestoneCompleted(m) {
  return m.is_completed === true;
}

/**
 * Formate les phases + jalons comme sur la fiche cas (fetchPhases).
 */
export function formatPhaseRow(phase) {
  const resolvedMilestones = (phase.milestones || [])
    .slice()
    .sort((a, b) => (a.milestone_order ?? 0) - (b.milestone_order ?? 0))
    .map((m) => ({
      id: m.id,
      name: m.milestone_name,
      description: m.description,
      note: m.note ?? null,
      completion_type: m.completion_type ?? null,
      outcome_quantity: m.outcome_quantity ?? null,
      milestone_order: m.milestone_order,
      milestone_template_id: m.milestone_template_id ?? null,
      updated_at: m.updated_at,
      validated_at: m.validated_at ?? null,
      validated_by_user_id: m.validated_by_user_id ?? null,
      validatedByName: m.validated_by?.full_name ?? null,
      is_rejected: m.is_rejected ?? false,
      rejected_date: m.rejected_date ?? null,
      completed: resolveMilestoneCompleted(m),
      completedDate: m.completed_date
        ? new Date(m.completed_date).toLocaleDateString('fr-FR')
        : null,
    }));

  return {
    id: phase.id,
    phase_order: phase.phase_order,
    total_aligners: phase.total_aligners ?? null,
    status: phase.status,
    type: (phase.phase_name || '').toLowerCase().replace(/\s+/g, '_'),
    name: phase.phase_name,
    group: phase.group || null,
    design_review_url: phase.design_review_url || null,
    description: phase.description,
    responsible_team: phase.responsible_team,
    responsible_user_id: phase.responsible_user_id ?? null,
    milestones: resolvedMilestones,
    _raw: phase,
  };
}

function isWorkshopPhaseRow(phase, groupKey) {
  const gNum = parseInt(String(groupKey), 10);
  if (!Number.isFinite(gNum)) return false;
  if (gNum === 1) {
    return (phase.phase_order ?? 0) >= GROUP1_WORKSHOP_MIN_PHASE_ORDER;
  }
  return gNum >= 2;
}

/**
 * Phases atelier pour un lot (groupe = current_phase). Lot 1 : ordre ≥ 4 (pré-production → expédition).
 */
export async function fetchProductionPhasesForLot(caseId, lotGroupKey) {
  if (!caseId || lotGroupKey == null || lotGroupKey === '') {
    return { phases: [], error: null };
  }

  const g = String(lotGroupKey);

  const { data, error } = await supabase
    .from('case_timeline_phases')
    .select(
      '*, milestones:phase_milestones(*, validated_by:user_profiles!phase_milestones_validated_by_user_id_fkey(full_name))'
    )
    .eq('case_id', caseId)
    .eq('group', g)
    .order('phase_order', { ascending: true });

  if (error) {
    console.error('[fetchProductionPhasesForLot]', error);
    return { phases: [], error };
  }

  const rows = (data || []).filter((p) => isWorkshopPhaseRow(p, g));
  const phases = rows.map(formatPhaseRow);
  return { phases, error: null };
}

/**
 * Phase « active » pour l’affichage atelier : in_progress d’abord, sinon première pending avec jalon incomplet, sinon dernière phase.
 */
export function pickActiveProductionPhase(phases) {
  if (!phases?.length) return null;
  const inProg = phases.find((p) => p.status === 'in_progress');
  if (inProg) return inProg;
  const pendingIncomplete = phases.find(
    (p) =>
      p.status === 'pending' &&
      (p.milestones || []).some((m) => !m.completed)
  );
  if (pendingIncomplete) return pendingIncomplete;
  return phases[phases.length - 1];
}

const CONCEPTION_INITIAL_MILESTONE_RE = /conception\s+initiale/i;
const CONCEPTION_PHASE_NAME_RE = /phase\s+de\s+conception|conception/i;

/**
 * Nombre d'aligneurs enregistré sur la phase de conception (jalon Conception initiale).
 * @param {string} caseId
 * @returns {Promise<number | null>}
 */
export async function fetchConceptionPhaseTotalAligners(caseId) {
  if (!caseId) return null;

  const { data: phases, error } = await supabase
    .from('case_timeline_phases')
    .select(
      'id, phase_name, phase_order, group, total_aligners, milestones:phase_milestones(milestone_order, milestone_name)'
    )
    .eq('case_id', caseId)
    .not('total_aligners', 'is', null);

  if (error) {
    console.error('[fetchConceptionPhaseTotalAligners]', error);
    return null;
  }
  if (!phases?.length) return null;

  const hasConceptionInitialeMilestone = (phase) =>
    (phase.milestones || []).some(
      (m) =>
        m.milestone_order === 1 && CONCEPTION_INITIAL_MILESTONE_RE.test(m.milestone_name || '')
    );

  const byMilestone = phases.find(hasConceptionInitialeMilestone);
  if (byMilestone?.total_aligners != null) return byMilestone.total_aligners;

  const byPhaseName = phases.find((p) => CONCEPTION_PHASE_NAME_RE.test(p.phase_name || ''));
  if (byPhaseName?.total_aligners != null) return byPhaseName.total_aligners;

  return phases[0].total_aligners ?? null;
}

/**
 * Propage les plages modèle UP/LOW « À » (moitié des aligneurs conception)
 * sur les lots encore aux plages par défaut (0–12) et non packagés.
 * @param {string} caseId
 * @param {number} totalAligners
 * @param {{ group?: string | number | null }} [opts]
 */
export async function syncEmptyProductionBatchesModelRanges(caseId, totalAligners, opts = {}) {
  if (!caseId || totalAligners == null || Number.isNaN(Number(totalAligners))) {
    return { error: null, updated: false };
  }

  const half = Math.floor(Number(totalAligners) / 2);
  if (!Number.isFinite(half) || half < 0) {
    return { error: null, updated: false };
  }

  const payload = {
    model_up_from: 0,
    model_up_to: half,
    model_low_from: 0,
    model_low_to: half,
    updated_at: new Date().toISOString(),
  };

  let query = supabase
    .from('production_batches')
    .update(payload)
    .eq('case_id', caseId)
    .is('packaging_date', null)
    .eq('model_up_to', 12)
    .eq('model_low_to', 12);

  if (opts.group != null && opts.group !== '') {
    query = query.eq('current_phase', String(opts.group));
  }

  const { error } = await query;
  if (error) {
    console.error('[syncEmptyProductionBatchesModelRanges]', error);
    return { error, updated: false };
  }
  return { error: null, updated: true };
}

/** @deprecated Utiliser syncEmptyProductionBatchesModelRanges — l’OF n’est plus initialisé depuis la conception. */
export async function syncEmptyProductionBatchesOfNumber(caseId, ofNumber, opts = {}) {
  return syncEmptyProductionBatchesModelRanges(caseId, ofNumber, opts);
}
